import React, { useEffect, useState } from 'react';
import SubjectMenuListCard from '@components/navigation/SubjectMenuListCard';
import {
  Location,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import BottomNavbar from '@components/navigation/BottomNavbar';
import { NavbarList } from '@components/navigation/NavigationList';
import { INavbarItem } from '@models/interfaces/navigation.interface';
import TopNavbar from '@components/navigation/TopNavbar';
import AccountSection, {
  IBubbleTransaction,
} from '@components/navigation/AccountSection';
import {
  useBubbleTransactionData,
  useSubjectMenuList,
} from '@hooks/student.hook';
import { convertToSlug } from '@utils/helpers/convertToSlug';
import { useIsJCUser, useUserDetails } from '@hooks/user.hook';
import { DetailUser } from '@models/types/users.type';
import Cookies from 'js-cookie';
import { Container } from 'geniebook-ui/dist';
import { subjectIdFromName } from '@utils/helpers/subjectIdFromName';
import { useQueryClient } from '@tanstack/react-query';
import { CDN_URL } from '@utils/constants/url.constants';
import { USER_TYPE_PARENT } from '@utils/constants/user.constants';
import ArrowLeft from '@assets/icons/arrow-left';
import { doLogout } from '@utils/logout';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { useAtom, useAtomValue, useStore } from 'jotai';
import moment from 'moment';
import {
  showCompleteWorksheetAtom,
  showGenieAskFloatingButtonAtom,
} from '@atom/worksheetAtom';
import { activeSubjectExpiredAtom } from '@atom/activeSubjectStore';
import { TOUR_ONBOARDING_FLAG } from '@utils/constants/tour.constants';
import {
  getExpiryDate,
  getSelectedSubscription,
  isSubscriptionDelayed,
  isSubscriptionTrial,
} from '@utils/helpers/subscriptionStatus';
import {
  isBottomSheetVisible,
  isSearching,
  NavbarState,
} from '@atom/navbarAtom';
import {
  IDelayedStartDateSubs,
  IExpSubsDate,
  ITrialSubs,
} from '@models/interfaces/local_storage.interface';
import ParentSubjectMenu from '@components/navigation/ParentSubjectMenu';
import { useStudentSubjectMenuList } from '@hooks/parent.hook';
import MagnifyingGlass from '@assets/icons/magnifying-glass';
import GenieaskPreviewImageOpen from '@components/GenieaskPreviewImageOpen';
import { subjectSubscriptionsQueryAtom } from '@atom/globalQueryAtom';
import SliderSolid from '@assets/icons/slider-solid';
// import ParentSubjectMenu from './ParentSubjectMenu';
// import { useStudentSubjectMenuList } from '@hooks/parent.hook';
// import GeniebookLogo from '@assets/illustration/auth/geniebook-logo';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

export interface NavbarProps {
  showAccountDropdown: boolean;
  setShowAccountDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  isShowMobileBubbleDropdown: boolean;
  setIsShowMobileBubbleDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  topOffset?: string;
}

/*
Do not put any logic regarding what to show in navbar in each page here.
Instead, you have to handle it inside pages using global navigation state.
If not defined, it will use default state
for example on how to define global navigation state, see src/app/pages/badges.tsx
*/

export const goToHome = (
  location: Location,
  navigate: ReturnType<typeof useNavigate>,
  getSubjectId: () => string = () => 'mathematics',
) => {
  if (location.pathname.substring(0, 22) === `/login/password_reset/`) {
    navigate('/');
  }
  if (location.pathname === `/${getSubjectId()}/worksheets`) {
    navigate(`/${getSubjectId()}/worksheets`);
    navigate(0);
  } else {
    navigate(`/${getSubjectId()}/worksheets`);
  }
};

const NavBar = (props: NavbarProps) => {
  const navbarState = useAtomValue(NavbarState);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const userType = Cookies.get('user_type');
    if (userType === USER_TYPE_PARENT) {
      if (!location.pathname.includes('/parent')) {
        navigate('/parent');
      }
    } else {
      if (location.pathname === '/parent') {
        navigate('/mathematics/worksheets');
      }
    }
  }, []);

  const { subjectId, menuId } = useParams();
  // detects current page to properly handle JC user identification in both Profile (general page) and Dashboard (a page with a subject in the URL)
  const isGeneralPage = () => {
    const firstSegment = location.pathname.split('/').filter(Boolean)[0];
    return subjectIdFromName(firstSegment) === 0;
  };

  const { isJC } = useIsJCUser({
    subjectId: subjectIdFromName(subjectId ?? ''),
    generalPage: isGeneralPage(),
  });
  const showLogo = isGeneralPage() || !isJC;
  const [showGenieAskMenu] = useAtom(showGenieAskFloatingButtonAtom);
  const queryClient = useQueryClient();
  const itemList = NavbarList({ showGenieAskInBottomNavbar: showGenieAskMenu });
  if (menuId) {
    for (let i = 0; i < itemList.length; i++) {
      itemList[i].selected = itemList[i].url === menuId;
    }
  }
  const width = useViewWidth();
  const [list, setList] = useState<INavbarItem[]>(
    NavbarList({ showGenieAskInBottomNavbar: showGenieAskMenu }),
  );

  const [dataDetailUser, setDetailUser] = useState<DetailUser | null>(null);
  const [bubbleTransaction, setBubbleTransaction] =
    useState<IBubbleTransaction>({
      bubble_earned: 0,
      bubble_spent: 0,
    });
  const getUserDetails = useUserDetails();
  const getBubbleTransaction = useBubbleTransactionData();
  const [showCompleteWorksheet, setShowCompleteWorksheet] = useAtom(
    showCompleteWorksheetAtom,
  );
  const { data: student_subject_menu_list } = useStudentSubjectMenuList(
    navbarState.haveParentSubjectMenu,
  );
  const store = useStore();

  const checkActiveBottomNavIcon = (list: INavbarItem[]): INavbarItem[] => {
    const tmpItems = JSON.parse(JSON.stringify(list)) as INavbarItem[];
    for (let i = 0; i < tmpItems.length; i++) {
      if (matchPath(tmpItems[i].path, location.pathname) != null)
        tmpItems[i].selected = true;
      else tmpItems[i].selected = false;
    }

    return tmpItems;
  };

  useEffect(() => {
    // comment this since its made hide menu buggy
    // setActiveByLocation();
    if (!matchPath('/:subjectId/worksheets', location.pathname)) {
      setShowCompleteWorksheet(false);
    }

    setList(checkActiveBottomNavIcon(list));
  }, [location.pathname]);

  const toHome = () => {
    goToHome(location, navigate, getSubjectId);
  };

  const querySubjectMenuList = useSubjectMenuList();
  const [subjectListLength, setSubjectListLength] = useState<number>(0);

  const fullLogoCase =
    width < breakpoint.lg
      ? `${CDN_URL}assets/icons/navigation/geniebook-logo-full-inverted.svg`
      : `${CDN_URL}assets/icons/navigation/geniebook-logo-full.svg`;

  const logoToUse = () => {
    return isJC
      ? `${CDN_URL}assets/icons/navigation/afterskool-logo-full.svg`
      : navbarState.haveFullLogo
      ? fullLogoCase
      : `${CDN_URL}assets/icons/navigation/logo-simplified.svg`;
  };

  // function setActiveByLocation() {
  //   const tmpItems: INavbarItem[] = [...itemList];
  //   for (let i = 0; i < tmpItems.length; i++) {
  //     let search = -1;
  //     if (tmpItems[i].relateToSubject) {
  //       search = location.pathname.search(
  //         new RegExp('/[a-z]*/' + tmpItems[i].url),
  //       );
  //     } else {
  //       search = location.pathname.search('/' + tmpItems[i].url);
  //     }

  //     if (location.pathname === '/badges' && tmpItems[i].key === 'account') {
  //       tmpItems[i].selected = true;
  //     } else {
  //       tmpItems[i].selected = search > -1;
  //     }
  //   }
  //   setList(tmpItems);
  // }

  // when get the user subject menu detail, check the permission
  useEffect(() => {
    if (!querySubjectMenuList.data?.data.error) {
      const newList: INavbarItem[] = NavbarList({
        showGenieAskInBottomNavbar: showGenieAskMenu,
      });
      const subjectMenu = querySubjectMenuList.data?.data?.data?.find(
        subject =>
          parseInt(subject.subject_id) == subjectIdFromName(subjectId || ''),
      );
      if (subjectMenu) {
        newList.forEach((item: INavbarItem, index: number) => {
          newList[index].hide = !subjectMenu.menu_list.includes(item.menuId);
          if (
            location.pathname === '/badges' ||
            location.pathname === '/leaderboard'
          ) {
            newList[index].selected = newList[index].key === 'account';
          }
        });
      }
      if (
        location.pathname === '/badges' ||
        location.pathname === '/leaderboard'
      ) {
        newList.forEach((item: INavbarItem, index: number) => {
          newList[index].selected = newList[index].key === 'account';
        });
      }

      // check the selected item
      const filteredList = checkActiveBottomNavIcon(newList);
      setList(filteredList);
    }
  }, [
    querySubjectMenuList.data?.data,
    subjectId,
    showGenieAskMenu,
    location.pathname,
  ]); //! TODO: exhaustive deps, fix the logic before it's going to explode

  useEffect(() => {
    if (getUserDetails?.data?.data) {
      setDetailUser(getUserDetails.data.data);
    }
  }, [getUserDetails]);

  useEffect(() => {
    if (getBubbleTransaction?.data?.data?.error === false) {
      setBubbleTransaction(getBubbleTransaction.data.data.data);
    }
  }, [getBubbleTransaction]);

  useEffect(() => {
    if (querySubjectMenuList.data?.data?.error === false) {
      setSubjectListLength(querySubjectMenuList.data.data.data.length);
    }
  }, [querySubjectMenuList]);

  const getSubjectId = () => {
    if (subjectId) return subjectId;
    const subject = querySubjectMenuList.data?.data?.data[0]?.display_name;
    if (subject) {
      return convertToSlug(subject);
    }
    return 'mathematics';
  };

  function handleClickNavigation(key: string) {
    const newList = list;
    let toNavigate = '';
    let relateToSubject = true;
    for (let i = 0; i < newList.length; i++) {
      if (newList[i].key === key) {
        toNavigate = newList[i].url;
        relateToSubject = newList[i].relateToSubject;
        break;
      }
    }

    if (toNavigate) {
      if (relateToSubject) {
        navigate(`/${getSubjectId()}/${toNavigate}`);
      } else {
        navigate(`/${toNavigate}`);
      }
    }
  }

  function logoutUser() {
    getUserDetails.remove();
    querySubjectMenuList.remove();

    // to reset all the query when logout
    doLogout(navigate, queryClient);
  }

  const [, setActiveSubjectExpired] = useAtom(activeSubjectExpiredAtom);
  const subjectSubscription = useAtomValue(subjectSubscriptionsQueryAtom);
  /*
   * check for:
   * - trial subscription > set localStorage
   * - delayed start > redirects to 099-03 page
   * - expired subscription > set localStorage & disable all onboarding
   */
  useEffect(() => {
    // skip validation only if has subjectId URL parameter
    if (!subjectId) {
      setActiveSubjectExpired(false);
      return;
    }

    setActiveSubjectExpired(null);

    const subjectMenuArr = querySubjectMenuList.data?.data?.data;
    if (!Array.isArray(subjectMenuArr)) return;

    const subjectSubscriptionArr = subjectSubscription.data?.data?.data;
    if (!Array.isArray(subjectSubscriptionArr)) return;

    // set trial subscription
    const tmpLocalStorageTrialSubs: ITrialSubs = JSON.parse(
      localStorage.getItem('trial_subs') ?? '{}',
    );
    if (![0, 1].includes(tmpLocalStorageTrialSubs[subjectId])) {
      // only set if not set before (value is not 0 / 1)
      tmpLocalStorageTrialSubs[subjectId] = isSubscriptionTrial(
        subjectId,
        subjectMenuArr,
        subjectSubscriptionArr,
      )
        ? 1
        : 0;
      localStorage.setItem(
        'trial_subs',
        JSON.stringify(tmpLocalStorageTrialSubs),
      );
    }

    if (
      isSubscriptionDelayed(subjectId, subjectMenuArr, subjectSubscriptionArr)
    )
      navigate(`/${getSubjectId()}/worksheets`);

    const tmpLocalStorageExpSubs: IExpSubsDate = JSON.parse(
      localStorage.getItem('exp_subs_date') ?? '{}',
    );

    const tmpLocalStorageDelayedStartDate: IDelayedStartDateSubs = JSON.parse(
      localStorage.getItem('delayed_subs_date') ?? '{}',
    );

    const isSubjectSubscriptionDelayed = isSubscriptionDelayed(
      subjectId,
      subjectMenuArr,
      subjectSubscriptionArr,
    );

    if (isSubjectSubscriptionDelayed) {
      const subscriptionData = getSelectedSubscription(
        subjectId,
        subjectMenuArr,
        subjectSubscriptionArr,
      );

      tmpLocalStorageDelayedStartDate[subjectId] = subscriptionData
        ? subscriptionData['delayed_start_date']
        : '';

      localStorage.setItem(
        'delayed_subs_date',
        JSON.stringify(tmpLocalStorageDelayedStartDate),
      );
    }

    const expiryDate = getExpiryDate(
      subjectId,
      subjectMenuArr,
      subjectSubscriptionArr,
    );
    if (moment(expiryDate).valueOf() < moment().valueOf()) {
      /*
       * disable onboarding
       * use localStorage instead of atom because getting react hooks issue
       * tour element at DashboardLayout rendered only when activeSubjectExpired is not null
       */
      localStorage.setItem(TOUR_ONBOARDING_FLAG, '1');
      localStorage.setItem('tourWsCompletedIdentifier', '1');
      localStorage.setItem('mcqTourIdentifier', '1');
      localStorage.setItem('sqTourIdentifier', '1');
      localStorage.setItem('alreadyTakeProgressTour', '1');
    }

    // update expiry localstorage
    tmpLocalStorageExpSubs[subjectId] = expiryDate;
    localStorage.setItem(
      'exp_subs_date',
      JSON.stringify(tmpLocalStorageExpSubs),
    );

    setActiveSubjectExpired(
      moment(tmpLocalStorageExpSubs[subjectId]).valueOf() < moment().valueOf(),
    );
  }, [querySubjectMenuList.data?.data, subjectId, subjectSubscription.data]);

  function handleBackButton() {
    const accountMenuLocation = [
      '/badges',
      '/profile',
      '/account-settings',
      '/exam-result',
      '/leaderboard',
      '/subscription',
    ];

    const checkLocation = (item: string) => {
      return item === location.pathname;
    };

    if (accountMenuLocation.find(checkLocation)) {
      props.setShowAccountDropdown(true);
    } else {
      navigate(-1);
    }
  }

  return (
    <div
      id="navbar"
      className={'sticky w-full left-0 z-100'}
      style={{ top: props.topOffset ?? 0 }}
    >
      <header className="max-md:!py-1 py-4 max-md:h-10 lg:bg-neutral-whiteboard bg-[#16133E]">
        <Container className="flex justify-start items-center max-lg:!px-4">
          {navbarState.haveBackButton && width < breakpoint.lg ? (
            <ArrowLeft
              className={
                'fill-current text-primary-apricot w-4 lg:hidden ' +
                (navbarState.haveSearchButton && width < breakpoint.md
                  ? 'mr-3'
                  : '')
              }
              onClick={() =>
                showCompleteWorksheet
                  ? setShowCompleteWorksheet(false)
                  : handleBackButton()
              }
              data-testid={'back-button'}
            />
          ) : (
            showLogo && (
              <div
                className={
                  'h-6 max-lg:text-neutral-whiteboard max-md:!h-8 max-md:p-2 max-md:-ml-2 max-md:inline-flex max-md:items-center max-md:justify-center flex-initial md:mr-4 ' +
                  (navbarState.haveFullLogo
                    ? 'w-[150px]'
                    : 'max-md:w-[32px] md:w-[24px]')
                }
                onClick={toHome}
              >
                <img
                  className={`${isJC ? 'h-8' : 'h-6'} max-md:!h-4`}
                  src={logoToUse()}
                  alt={'geniebook'}
                />
              </div>
            )
          )}
          <div
            className={
              'gb-menus flex justify-start items-center max-w-[80%] ' +
              (width < breakpoint.lg ? 'mr-auto' : '')
            }
          >
            {navbarState.haveTitle && width < breakpoint.lg ? (
              <div
                className={
                  'font-black capitalize text-lg text-center text-ellipsis overflow-hidden whitespace-nowrap max-lg:pl-[23px] max-md:pl-2 max-md:!text-md w-full lg:text-primary-navyblue text-neutral-whiteboard'
                }
                data-testid={'page-title'}
              >
                {navbarState.titleName}
              </div>
            ) : navbarState.haveSubjectMenu ? (
              <SubjectMenuListCard />
            ) : navbarState.haveParentSubjectMenu &&
              student_subject_menu_list ? (
              <ParentSubjectMenu list={student_subject_menu_list} />
            ) : null}
            {width >= breakpoint.lg && subjectId ? (
              <div
                className={
                  'flex-initial mx-6 border-l border-neutral-notebook h-8'
                }
              ></div>
            ) : null}
            {width >= breakpoint.lg &&
            subjectId &&
            navbarState.haveTopNavbar ? (
              <div className={'flex-initial'}>
                <TopNavbar
                  items={list}
                  onClickNavigation={handleClickNavigation}
                />
              </div>
            ) : null}
          </div>
          {navbarState.haveLogoutButton ? (
            <div className="flex items-center lg:ml-auto">
              <button
                className="text-alert-error text-sm font-bold"
                onClick={logoutUser}
              >
                Logout
              </button>
            </div>
          ) : null}
          {navbarState.haveSearchButton && width < breakpoint.md ? (
            <MagnifyingGlass
              className={
                'fill-current text-primary-apricot w-5 mr-3 cursor-pointer'
              }
              onClick={() => store.set(isSearching, true)}
            />
          ) : null}

          {navbarState.haveBottomSheet && width < breakpoint.lg && (
            <SliderSolid
              className="exclude-element text-primary-apricot cursor-pointer fill-current max-lg:flex max-lg:w-[18px] lg:hidden mr-1"
              onClick={() => store.set(isBottomSheetVisible, true)}
            />
          )}

          {navbarState.haveAccountSection ? (
            <AccountSection
              logoutUser={logoutUser}
              userData={dataDetailUser}
              bubbleTransaction={bubbleTransaction}
              showAccountDropdown={props.showAccountDropdown}
              setShowAccountDropdown={props.setShowAccountDropdown}
              isShowMobileBubbleDropdown={props.isShowMobileBubbleDropdown}
              setIsShowMobileBubbleDropdown={
                props.setIsShowMobileBubbleDropdown
              }
            />
          ) : null}
        </Container>
      </header>
      {width < breakpoint.lg && navbarState.haveBottomNavbar ? (
        <BottomNavbar
          items={list}
          setItems={setList}
          onClickNavigation={handleClickNavigation}
          showAccountDropdown={props.showAccountDropdown}
          setShowAccountDropdown={props.setShowAccountDropdown}
          subjectListLength={subjectListLength}
        />
      ) : null}
      <GenieaskPreviewImageOpen />
    </div>
  );
};

export default NavBar;
